/**
 * Expense create/edit — one component for both `new` and `:id/edit`, switched
 * on whether `useParams().id` is present. Field set matches `expenseCreateSchema`
 * (refactor/backend/src/modules/expenses/serializers.ts:63-71); create uses
 * `POST expenses/`, edit uses `PATCH expenses/{id}/`.
 *
 * `receipt` is not on this form: it is a Django `FileField` normally written
 * via multipart, the same deferred-upload pattern as property/maintenance
 * images (see the TODO on `expenseCreateSchema` in the backend serializer).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { createExpense, getExpense, updateExpense } from '../../api/endpoints/expenses';
import { EXPENSE_CATEGORIES } from '../../types/expense';
import type { ExpenseCategory, ExpenseCreateRequest } from '../../types/expense';

const INITIAL_FORM = {
  property: '',
  category: 'OTHER' as ExpenseCategory,
  amount: '',
  date: '',
  description: '',
  vendor: '',
};

type FormState = typeof INITIAL_FORM;

function toPayload(form: FormState): ExpenseCreateRequest {
  return {
    property: Number(form.property),
    category: form.category,
    amount: form.amount,
    date: form.date,
    description: form.description,
    vendor: form.vendor,
  };
}

export function ExpenseFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const expenseId = Number(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const existing = useQuery({
    queryKey: ['expense', expenseId],
    queryFn: () => getExpense(expenseId),
    enabled: isEdit && Number.isFinite(expenseId),
  });

  useEffect(() => {
    if (existing.data === undefined) return;
    const expense = existing.data;
    setForm({
      property: String(expense.property),
      category: expense.category,
      amount: expense.amount,
      date: expense.date,
      description: expense.description,
      vendor: expense.vendor,
    });
  }, [existing.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () =>
      isEdit ? updateExpense(expenseId, toPayload(form)) : createExpense(toPayload(form)),
    onSuccess: () => navigate('..', { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (isEdit && existing.isPending) return <p>Loading…</p>;
  if (isEdit && existing.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(existing.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>{isEdit ? 'Edit expense' : 'Log expense'}</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="expense-property-id">Property id</label>
        <input
          id="expense-property-id"
          type="number"
          required
          value={form.property}
          onChange={(event) => update('property', event.target.value)}
        />
        {fieldError('property') !== undefined && (
          <p className="form__field-error">{fieldError('property')}</p>
        )}

        <label htmlFor="expense-category-select">Category</label>
        <select
          id="expense-category-select"
          value={form.category}
          onChange={(event) => update('category', event.target.value)}
        >
          {EXPENSE_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="expense-amount">Amount</label>
        <input
          id="expense-amount"
          required
          value={form.amount}
          onChange={(event) => update('amount', event.target.value)}
        />
        {fieldError('amount') !== undefined && <p className="form__field-error">{fieldError('amount')}</p>}

        <label htmlFor="expense-date">Date</label>
        <input
          id="expense-date"
          type="date"
          required
          value={form.date}
          onChange={(event) => update('date', event.target.value)}
        />
        {fieldError('date') !== undefined && <p className="form__field-error">{fieldError('date')}</p>}

        <label htmlFor="expense-vendor">Vendor</label>
        <input
          id="expense-vendor"
          value={form.vendor}
          onChange={(event) => update('vendor', event.target.value)}
        />

        <label htmlFor="expense-description">Description</label>
        <textarea
          id="expense-description"
          required
          value={form.description}
          onChange={(event) => update('description', event.target.value)}
        />
        {fieldError('description') !== undefined && (
          <p className="form__field-error">{fieldError('description')}</p>
        )}

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
    </section>
  );
}

export default ExpenseFormPage;
